import { Injectable, Injector, inject } from '@angular/core';
import {
  BehaviorSubject,
  filter,
  from,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  shareReplay,
  startWith,
} from 'rxjs';
import { AmplifyConfigurationService } from './amplify-config.service';
import { ApiService } from '../api/api.service';
import { User } from '../../models/user.model';
import { UserLogin } from '../../models/user-login.model';
import { Hub } from 'aws-amplify/utils';
import { UserType } from '@shared/types/user-type';
import { catchError } from 'rxjs/operators';
import { CompanyService } from '../company/company.service';
import { CompanyContext } from '../business/company-context.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _currentUserSubject = new BehaviorSubject<User | null | undefined>(undefined);
  public currentUser$ = this._currentUserSubject.asObservable();

  private readonly _user$ = new BehaviorSubject<User | null | undefined>(undefined);
  public user$ = this._user$.asObservable();

  public isBusinessUser$: Observable<boolean> = this.user$.pipe(
    map((user) => user?.userType === UserType.BUSINESS),
  );

  public isConsumerUser$: Observable<boolean> = this.user$.pipe(
    map((user) => user?.userType === UserType.CONSUMER),
  );

  constructor(
    readonly cognitoService: AmplifyConfigurationService,
    private readonly injector: Injector,
  ) {
    this.refreshSession();
    this.listenToAuthEvents();
  }

  private listenToAuthEvents() {
    Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          // Apply pending Google userType intent before refreshing session
          // so the session immediately reflects the correct userType.
          this.applyPendingUserTypeIntent()
            .pipe(
              catchError((err) => {
                console.error('AuthService: Failed to apply pending user type intent', err);
                return of(null);
              }),
            )
            .subscribe(() => this.refreshSession());
          break;
        case 'signedOut':
          this._currentUserSubject.next(null);
          break;
        case 'tokenRefresh':
          this.refreshSession();
          break;
      }
    });
  }

  /**
   * Reads the pending Google sign-up userType from sessionStorage
   * (stored by signInWithGoogle before the OAuth redirect) and applies
   * it as a Cognito custom attribute. Clears the flag after use.
   *
   * This is a no-op when there is no pending intent (e.g. for regular sign-ins).
   */
  private applyPendingUserTypeIntent(): Observable<any> {
    const INTENT_KEY = 'fynd:pendingGoogleUserType';
    const COMPANY_KEY = 'fynd:pendingGoogleCompanyName';
    const pendingUserType =
      typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(INTENT_KEY) : null;
    const pendingCompanyName =
      typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(COMPANY_KEY) : null;

    if (!pendingUserType) {
      return of(null);
    }

    sessionStorage.removeItem(INTENT_KEY);
    if (pendingCompanyName) {
      sessionStorage.removeItem(COMPANY_KEY);
    }

    const attributes: any = { 'custom:userType': pendingUserType };
    if (pendingCompanyName) {
      attributes['custom:companyName'] = pendingCompanyName;
    }

    return from(this.cognitoService.updateAttributes(attributes)).pipe(
      switchMap(() => from(this.cognitoService.forceRefreshSession())),
    );
  }

  refreshSession(): Observable<User | null> {
    const session$ = this.cognitoService.isUserLogedIn().pipe(
      switchMap((user) => {
        if (user?.userType !== UserType.BUSINESS) {
          return of(user);
        }

        const companyService = this.injector.get(CompanyService);
        const companyContext = this.injector.get(CompanyContext);

        return companyService.getCompany().pipe(
          map((company) => {
            if (company?.company_uuid && user) {
              companyContext.setCompany({
                companyUuid: company.company_uuid,
                name: company.company_name,
              });
              user.companyUuid = company.company_uuid;
            }
            return user;
          }),
          catchError((err) => {
            console.error('AuthService: Error fetching company', err);
            return of(user);
          }),
          startWith(user),
        );
      }),
      tap((user) => {
        this._currentUserSubject.next(user);
        this._user$.next(user);
      }),
      shareReplay(1),
    );

    session$.subscribe();
    return session$;
  }

  setCurrentUser(user: User | null) {
    this._currentUserSubject.next(user);
  }

  userSession(): Observable<any> {
    return from(this.cognitoService.userAuthentication()).pipe(
      map((session) => {
        return session;
      }),
    );
  }
  signIn(user: UserLogin): Observable<any> {
    return from(this.cognitoService.signIn(user.username, user.password)).pipe(
      switchMap((res) => {
        if (res.isSignedIn) {
          return this.refreshSession().pipe(map(() => res));
        }
        return of(res);
      }),
    );
  }

  signUp(user: User): Observable<any> {
    return from(this.cognitoService.registerUser(user));
  }

  signOut(): Observable<any> {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('justLoggedOut', 'true');
    }
    return from(this.cognitoService.signOut()).pipe(
      map((res) => {
        this._currentUserSubject.next(null);
        this.injector.get(CompanyContext).clear();
        return res;
      }),
    );
  }

  /**
   * Initiates Google OAuth sign-in via Amplify.
   *
   * @param userType - The role selected on the **signup** form. Pass `undefined`
   *   (or omit) when called from the login page so the existing `custom:userType`
   *   attribute on the Cognito user is never overwritten.
   */
  signInWithGoogle(userType?: UserType, companyName?: string): void {
    if (userType && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('fynd:pendingGoogleUserType', userType);
      if (companyName) {
        sessionStorage.setItem('fynd:pendingGoogleCompanyName', companyName);
      }
    }
    this.cognitoService.signInWithGoogle();
  }

  private readonly apiService = inject(ApiService);

  isUserLogedIn(): Observable<boolean> {
    if (this._currentUserSubject.value !== undefined) {
      return of(!!this._currentUserSubject.value);
    }
    return this.currentUser$.pipe(
      filter((u): u is User | null => u !== undefined),
      map((u) => !!u),
      take(1),
    );
  }

  updateUser(attributes: any): Observable<any> {
    const payload = { ...attributes };
    if (payload.given_name) {
      payload.firstName = payload.given_name;
      delete payload.given_name;
    }
    if (payload.family_name) {
      payload.lastName = payload.family_name;
      delete payload.family_name;
    }
    if (payload.address) {
      payload.country = payload.address;
      delete payload.address;
    }

    return this.apiService
      .put('user', payload)
      .pipe(switchMap((res) => this.refreshSession().pipe(map(() => res))));
  }
}
